import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Modal, Header, Button, Popup, Container, Segment, Message, Icon } from 'semantic-ui-react';
//test change 2
import styles from './MakePayment.module.scss';
import moment from 'moment';
import { useTimer } from 'react-timer-hook';

const MakePayment = ({ open, onOpen, canMakePayment, policies, onClose, payment, paymentActions, inline, steps, onReset, error, onStepChange, isRecurring, allowMultipleEmailAddresses, defaultEmailAddress, emailAddressOptions, bypassAmountChecks, bypassEmailAddressCheck, maskErrorMessage, isMasked, isMaskFailed, hidePaymentOption, walletsDisabled, onPaymentComplete, hasBalance, isOneTime }) => {
	const [sources, setSources] = useState([]);
	const singlePaymentRef = useRef();

	const selectedTotalAmount = useMemo(() => (policies || []).reduce((acc, sp) => acc + sp.amount, null), [policies]);

	const [loading, setLoading] = useState(false);
    
	useEffect(() => {
		setSources([]);
		paymentActions.getPaymentSettings();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if(!walletsDisabled && !payment.settings.walletsLoaded && (policies || []).length > 0) {
			paymentActions.getPaymentWallets(policies.map(p => p.policyNumber));
		}

	}, [payment.settings.walletsLoaded, paymentActions, policies, walletsDisabled])

	const [step, setStep] = useState(0);

	useEffect(() => {
		if (onStepChange) onStepChange();
	}, [onStepChange, step]);


	const reset = useCallback(() => {
		setStep(0);
		setSources([]);

		if (onReset) onReset();
	}, [onReset]);

	const onCancel = useCallback(() => {
		reset();
		if (onClose) onClose(true);
		// WS- 924 on Cancel Button triggering close icon on app header
		document.getElementById("closeLink").click();
	}, [onClose, reset]);

	const context = useMemo(() => {
		return {
			policies,
			onPaymentInformationUpdate: setSources,
			sources,
			payment,
			paymentActions,
			selectedTotalAmount,
			singlePaymentRef,
			step,
			setStep,
			loading,
			setLoading,
			isRecurring,
			allowMultipleEmailAddresses,
			defaultEmailAddress,
			emailAddressOptions,
			onCancel,
			bypassAmountChecks,
			bypassEmailAddressCheck,
            onPaymentComplete,
			hidePaymentOption,
		};
	}, [policies, sources, payment, paymentActions, selectedTotalAmount, step, loading, isRecurring, allowMultipleEmailAddresses, defaultEmailAddress, emailAddressOptions, onCancel, bypassAmountChecks, bypassEmailAddressCheck, onPaymentComplete, hidePaymentOption]);

	const onBack = useCallback(() => setStep(step - 1), [step]);

	const currentStep = useMemo(() => steps[step](context), [context, step, steps]);
	const subheader = useMemo(() => currentStep.subheader, [currentStep]);
	const content = useMemo(() => currentStep.content, [currentStep]);
	const buttonText = useMemo(() => currentStep.alternateContinueButtonText || 'Continue', [currentStep]);
	const errors = useMemo(() => (currentStep.getErrors ? currentStep.getErrors(context) || [] : []), [currentStep, context]);
	const canContinue = useMemo(() => errors.length === 0, [errors]);
	const showBack = useMemo(() => (currentStep.showBack ? currentStep.showBack() : step !== 0), [currentStep, step]);
	const showCancel = useMemo(() => (currentStep.showCancel ? currentStep.showCancel() : !inline), [currentStep, inline]);

	const onContinue = useCallback(() => {
		if (canContinue) {
			if (step === steps.length - 1) {
				if(onClose) onClose();
			} else {
				if (currentStep.onContinue) {
					currentStep.onContinue();
				} else {
					setStep(step + 1);
				}
			}
		}
	}, [canContinue, currentStep, onClose, step, steps]);

	const earliestIssuedOn = useMemo(() => {
		const ordered = sources
			.filter(s => s.fiserv && s.fiserv.issuedOn)
			.map(s => s.fiserv.issuedOn)
			.sort((a, b) => a - b);
		return ordered.length > 0 && step !== steps.length - 1 ? ordered[0] : null;
	}, [sources, step, steps.length]);

	const earliestIssuedExpiresOn = useMemo(() => {
		if (earliestIssuedOn) {
			return moment(earliestIssuedOn).add(10, 'minutes');
		} else {
			return null;
		}
	}, [earliestIssuedOn]);

	const onExpire = useCallback(() => {
		alert('Time has expired, if you wish to continue, please start over.');
		onCancel();
	}, [onCancel]);

	const { seconds, minutes, start, restart, pause } = useTimer({ onExpire });

	function renderErrorsContent() {
		if (errors.length > 0) {
			return (
				<ul className={errors.length <= 1 ? styles['list-unstyled'] : ''}> 
					{errors.map((e, i) => {
						return <li key={i}>{e}</li>;
					})}
				</ul>
			);
		}

		return null;
	}

	useEffect(() => {
		if (earliestIssuedExpiresOn !== null) {
			restart(earliestIssuedExpiresOn);
			start();
		}

		return () => {
			if (earliestIssuedExpiresOn) {
				pause();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [earliestIssuedExpiresOn]);

	function renderHeader() {
		return (
			<React.Fragment>
				<Header className={styles.start} icon='credit card alternative' content={isRecurring ? 'Setup Recurring Payment' : 'Pay Now'} subheader={subheader} />
				{earliestIssuedOn != null && (
					<div className={styles.end}>
						<div>Time Left: {`${minutes}:${String(seconds).padStart(2, '0')}`}</div>
					</div>
				)}
			</React.Fragment>
		);
	}

	function renderActions() {
		const isRecurOrOneTime = isRecurring || isOneTime;
		return (
			<React.Fragment>
				{showBack && (isRecurOrOneTime || hasBalance) ? <Button floated='left' content='Back' disabled={loading} onClick={onBack} /> : null}
				{showCancel && (isRecurOrOneTime || hasBalance) ? <Button content='Cancel' onClick={onCancel} disabled={loading} /> : null}
				 {isRecurOrOneTime && <Popup
					trigger={
						<span>
							<Button className={styles.continue} disabled={!canContinue || loading} primary content={buttonText} onClick={onContinue} />
						</span>
					}
					on={['hover', 'click']}
					content={renderErrorsContent()}
					disabled={canContinue}
					position='top right'
				/>}
				{!isRecurOrOneTime && <Popup
					trigger={
						<span>
							<Button className={styles.continue} disabled={(!canContinue && hasBalance) || loading} primary content={buttonText} onClick={hasBalance ? onContinue : onCancel} />
						</span>
					}
					on={['hover', 'click']}
					content={renderErrorsContent()}
					disabled={canContinue || !hasBalance}
					position='top right'
				/>}
			</React.Fragment>
		);
	}

	function renderModal() {
		if (!canMakePayment) {
			return (
				<Modal open={open} onOpen={onOpen} size=' '>
					<Header icon='credit card alternative' content='Pay Now' subheader='Cannot accept payments at this time' />
					<Modal.Content>
						<Header as='h4' content='We are currently experiencing issues accepting payments.' />
						<Header as='h4' content='Please contact Customer Service at 800.477.1660.' />
						<Header as='h4' content='We apologize for the inconvenience.' />
					</Modal.Content>
					<Modal.Actions>
						<Button content='OK' onClick={onCancel} disabled={loading} />
					</Modal.Actions>
				</Modal>
			);
		}

		return (
			<Modal open={open} onOpen={onOpen} size=' '>
				<Modal.Header className={styles.header}>{renderHeader()}</Modal.Header>
				<Modal.Content scrolling className={styles.content}>
					{isMasked && (
						<Header as='h5' block textAlign='center' color='green'>
							<Icon name="phone" />
                        Recording of this call is currently masked.
						</Header>
					)}
					{isMaskFailed && (
						<Header as='h5' block textAlign='center' color='red'>
							<Icon name="phone" />Failed to mask recording of this call: {maskErrorMessage}
						</Header>
					)}
					{error ? <Message error content={error} /> : null}
					{content}
				</Modal.Content>
				<Modal.Actions>{renderActions()}</Modal.Actions>
			</Modal>
		);
	}

	function renderInline() {
		if (!canMakePayment) {
			return (
				<React.Fragment>
					<Header as='h4' content='We are currently experiencing issues accepting payments.' />
					<Header as='h4' content='Please contact Customer Service at 800.477.1660.' />
					<Header as='h4' content='We apologize for the inconvenience.' />
				</React.Fragment>
			);
		}

		return (
			<Container>
				<Segment.Group>
					<Segment attached padded>
						{renderHeader()}
					</Segment>
					<Segment attached padded='very' className={styles.inlineContent}>
						{isMasked && (
							<Header as='h5' block textAlign='center' color='green'>
								<Icon name="phone" />
                                    Recording of this call is currently masked.
							</Header>
						)}
						{isMaskFailed && (
							<Header as='h5' block textAlign='center' color='red'>
								<Icon name="phone" />Failed to mask recording of this call: {maskErrorMessage}
							</Header>
						)}
						{error ? <Message error content={error} /> : null}
						{content}
					</Segment>
					<Segment attached className={styles.buttonsegment}>
						{renderActions()}
					</Segment>
				</Segment.Group>
			</Container>
		);
	}

	return inline ? renderInline() : renderModal();
};

MakePayment.propTypes = {
	onClose: PropTypes.func,
	onOpen: PropTypes.func,
	inline: PropTypes.bool,
	steps: PropTypes.array.isRequired,
	loading: PropTypes.bool,
	onReset: PropTypes.func,
	open: PropTypes.bool,
	canMakePayment: PropTypes.bool.isRequired,
	policies: PropTypes.array,
	payment: PropTypes.object.isRequired,
	paymentActions: PropTypes.object.isRequired,
	error: PropTypes.string,
	onStepChange: PropTypes.func,
	bypassAmountChecks: PropTypes.bool,
	bypassEmailAddressCheck: PropTypes.bool,
    onPaymentComplete: PropTypes.func,
	hasBalance: PropTypes.bool,
};

export default MakePayment;
